import React, { useEffect, useState } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { adminApi } from '../utils/api';
import { StatCard, Card, fmtDate } from '../components/UI';
import RocketLoader from '../components/RocketLoader';

const PIE_COLORS = ['#EF1D28', '#FB923C', '#059669', '#DC2626', '#D97706', '#0891B2'];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 8, padding: '8px 12px' }}>
      <div style={{ color: '#64748B', fontSize: 11 }}>{label}</div>
      {payload.map((p, i) => <div key={i} style={{ color: p.color || '#FB923C', fontWeight: 700 }}>{p.value}</div>)}
    </div>
  );
};

const TYPE_ICONS = { resume: '📄', cover_letter: '✉️', cv: '🎓' };

export const DashboardPage = () => {
  const [stats,    setStats]    = useState(null);
  const [activity, setActivity] = useState(null);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    Promise.all([
      adminApi.getStats(),
      adminApi.getActivity().catch(() => null),
    ]).then(([s, a]) => { setStats(s); setActivity(a); }).finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300 }}>
      <RocketLoader size={160} message="Loading stats…" />
    </div>
  );

  if (!stats) return null;

  const docTypeData = stats.docsByType?.map(d => ({ name: d._id, value: d.count })) || [];
  const templateData = stats.templateUsage?.map(d => ({ name: d._id || 'unknown', count: d.count })) || [];

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ margin: '0 0 4px', color: '#0F172A', fontSize: 22, fontWeight: 800 }}>Dashboard</h1>
        <p style={{ margin: 0, color: '#64748B', fontSize: 13 }}>Platform overview and analytics</p>
      </div>

      {/* Stat Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6,1fr)', gap: 16, marginBottom: 24 }}>
        <StatCard icon="👥" label="Total Users"     value={stats.totalUsers}    sub={`+${stats.newUsersThisMonth} this month`} trend={stats.newUsersThisWeek} color="#EF1D28" />
        <StatCard icon="⭐" label="Pro Users"       value={stats.proUsers}      sub={`${stats.freeUsers} on free plan`}        color="#FB923C" />
        <StatCard icon="💰" label="Monthly Revenue" value={`$${stats.revenue?.monthly?.toLocaleString()}`} sub={`$${stats.revenue?.annual?.toLocaleString()} annual`} color="#059669" />
        <StatCard icon="📄" label="Total Documents" value={stats.totalDocs}     sub={`${stats.totalAdmins} admin accounts`}   color="#D97706" />
        <StatCard icon="🎤" label="Interviews"      value={stats.totalInterviews ?? '—'} sub="AI interview sessions"         color="#7E22CE" />
        <StatCard icon="💬" label="Pending Reviews" value={stats.pendingFeedback ?? '—'} sub="feedback to review"           color="#0EA5E9" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16, marginBottom: 16 }}>
        {/* User Growth Chart */}
        <Card>
          <div style={{ color: '#0F172A', fontWeight: 700, marginBottom: 16 }}>User Growth (Last 30 Days)</div>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={stats.userGrowth || []}>
              <XAxis dataKey="_id" tick={{ fill: '#64748B', fontSize: 11 }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fill: '#64748B', fontSize: 11 }} tickLine={false} axisLine={false} />
              <Tooltip content={<CustomTooltip />} />
              <Line type="monotone" dataKey="count" stroke="#EF1D28" strokeWidth={2.5} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </Card>

        {/* Doc Types Pie */}
        <Card>
          <div style={{ color: '#0F172A', fontWeight: 700, marginBottom: 16 }}>Documents by Type</div>
          {docTypeData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={160}>
                <PieChart>
                  <Pie data={docTypeData} cx="50%" cy="50%" innerRadius={45} outerRadius={70} dataKey="value" paddingAngle={3}>
                    {docTypeData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
                {docTypeData.map((d, i) => (
                  <div key={d.name} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11 }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: PIE_COLORS[i % PIE_COLORS.length] }} />
                    <span style={{ color: '#64748B' }}>{d.name}: <strong style={{ color: '#0F172A' }}>{d.value}</strong></span>
                  </div>
                ))}
              </div>
            </>
          ) : <div style={{ color: '#64748B', textAlign: 'center', paddingTop: 40 }}>No documents yet</div>}
        </Card>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        {/* Template Usage */}
        <Card>
          <div style={{ color: '#0F172A', fontWeight: 700, marginBottom: 16 }}>Top Templates Used</div>
          {templateData.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={templateData} layout="vertical">
                <XAxis type="number" tick={{ fill: '#64748B', fontSize: 11 }} tickLine={false} axisLine={false} />
                <YAxis type="category" dataKey="name" tick={{ fill: '#64748B', fontSize: 11 }} tickLine={false} axisLine={false} width={70} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="count" fill="#FB923C" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <div style={{ color: '#64748B', textAlign: 'center', paddingTop: 40 }}>No template data</div>}
        </Card>

        {/* Plan Distribution */}
        <Card>
          <div style={{ color: '#0F172A', fontWeight: 700, marginBottom: 16 }}>Plan Distribution</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 8 }}>
            {[
              { label: 'Free Users', value: stats.freeUsers, total: stats.totalUsers, color: '#E2E8F0' },
              { label: 'Pro Users',  value: stats.proUsers,  total: stats.totalUsers, color: '#FB923C' },
            ].map(item => {
              const pct = stats.totalUsers > 0 ? Math.round((item.value / stats.totalUsers) * 100) : 0;
              return (
                <div key={item.label}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span style={{ color: '#64748B', fontSize: 13 }}>{item.label}</span>
                    <span style={{ color: '#0F172A', fontWeight: 700 }}>{item.value} <span style={{ color: '#64748B', fontWeight: 400 }}>({pct}%)</span></span>
                  </div>
                  <div style={{ background: '#F8FAFC', borderRadius: 4, height: 8, overflow: 'hidden' }}>
                    <div style={{ width: `${pct}%`, height: '100%', background: item.color, borderRadius: 4, transition: 'width 1s ease' }} />
                  </div>
                </div>
              );
            })}
            <div style={{ marginTop: 8, padding: '12px', background: '#F8FAFC', borderRadius: 8 }}>
              <div style={{ color: '#64748B', fontSize: 12, marginBottom: 4 }}>Conversion Rate</div>
              <div style={{ color: '#15803D', fontWeight: 800, fontSize: 20 }}>
                {stats.totalUsers > 0 ? ((stats.proUsers / stats.totalUsers) * 100).toFixed(1) : 0}%
              </div>
              <div style={{ color: '#64748B', fontSize: 11 }}>Free → Pro</div>
            </div>
          </div>
        </Card>
      </div>

      {/* Activity Feed */}
      {activity && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          {/* Recent Signups */}
          <Card>
            <div style={{ color: '#0F172A', fontWeight: 700, marginBottom: 14 }}>🆕 Recent Signups</div>
            {activity.recentUsers?.length === 0
              ? <div style={{ color: '#94A3B8', fontSize: 13, textAlign: 'center', padding: '20px 0' }}>No recent signups</div>
              : activity.recentUsers?.map(u => (
                <div key={u._id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid #F8FAFC' }}>
                  {u.avatar
                    ? <img src={u.avatar} alt="" style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                    : <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'linear-gradient(135deg,#EF1D28,#FB923C)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13, flexShrink: 0 }}>{u.name?.charAt(0)}</div>}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#0F172A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.name}</div>
                    <div style={{ fontSize: 11, color: '#94A3B8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email}</div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 20, background: u.plan === 'pro' ? '#FFF7ED' : '#F8FAFC', color: u.plan === 'pro' ? '#FB923C' : '#94A3B8' }}>{u.plan || 'free'}</div>
                    <div style={{ fontSize: 10, color: '#CBD5E1', marginTop: 2 }}>{fmtDate(u.createdAt)}</div>
                  </div>
                </div>
              ))}
          </Card>

          {/* Recent Documents Created */}
          <Card>
            <div style={{ color: '#0F172A', fontWeight: 700, marginBottom: 14 }}>📄 Recent Documents</div>
            {activity.recentDocs?.length === 0
              ? <div style={{ color: '#94A3B8', fontSize: 13, textAlign: 'center', padding: '20px 0' }}>No recent documents</div>
              : activity.recentDocs?.map(doc => (
                <div key={doc._id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid #F8FAFC' }}>
                  <span style={{ fontSize: 20, flexShrink: 0 }}>{TYPE_ICONS[doc.type] || '📄'}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#0F172A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {doc.data?.personalInfo?.fullName || doc.title || 'Untitled'}
                    </div>
                    <div style={{ fontSize: 11, color: '#94A3B8' }}>{doc.userId?.name || 'Unknown'} · {doc.type}</div>
                  </div>
                  <div style={{ fontSize: 10, color: '#CBD5E1', flexShrink: 0 }}>{fmtDate(doc.createdAt)}</div>
                </div>
              ))}
          </Card>
        </div>
      )}
    </div>
  );
};
